import {
  useEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from "react";
import Button from "../UI/Button";
import Text from "../UI/Text";
import { IconEraser } from "@tabler/icons-react";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import styles from "./SignaturePad.module.css";

type SignaturePadProps = {
  label: string;
  onChange: (dataUrl: string | null) => void;
  compact?: boolean;
};

export default function SignaturePad({
  label,
  onChange,
  compact = false,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const hasInk = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#1b2a4a";
    }
  }, []);

  function pointerPos(e: ReactPointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handleDown(e: ReactPointerEvent<HTMLCanvasElement>) {
    drawing.current = true;
    last.current = pointerPos(e);
    canvasRef.current?.setPointerCapture(e.pointerId);
  }

  function handleMove(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx || !last.current) return;
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    hasInk.current = true;
  }

  function handleUp() {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current && canvasRef.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  }

  function handleClear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    hasInk.current = false;
    onChange(null);
  }

  return (
    <div className={styles.block}>
      <Text variant="deci" weight="bold">
        {label}
      </Text>
      <div
        className={classnames(
          styles.canvasWrap,
          compact && styles.canvasWrapCompact,
        )}
      >
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          onPointerDown={handleDown}
          onPointerMove={handleMove}
          onPointerUp={handleUp}
          onPointerLeave={handleUp}
        />
      </div>
      <Button
        type="button"
        variant="tertiary"
        size="sm"
        icon={IconEraser}
        onClick={handleClear}
      >
        Effacer
      </Button>
    </div>
  );
}
